import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { OpbCompanySearchBoxComponent } from '../opb-company-search-box/opb-company-search-box.component';
import { OpbCompanyCsvImportDialogComponent } from '../opb-company-csv-import-dialog/opb-company-csv-import-dialog.component';
import { OpbCompanyService } from '../../../services/opb-company.service';
import { OpbCompanyDto, OpbCompaniesSearchRequest } from '../../../models/api/opbCompanyDto';
import { OpbCompanySearchFilter } from '../../../models/opbCompanySearchFilter';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { Helpers } from '../../../utilities/helpers';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { IAppState } from '../../../store/states/app.state';
import { SetOpbCompanySearchFilter, SetOpbCompanyPagination, SetOpbCompanySort } from '../../../store/actions/opb-company-list.actions';
import { selectOpbCompanyListFullState } from '../../../store/selectors/opb-company-list.selectors';

@Component({
  selector: 'app-opb-company-list',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    OpbCompanySearchBoxComponent,
    GenericListComponent,
    NgxSpinnerModule
  ],
  templateUrl: './opb-company-list.component.html',
  styleUrl: './opb-company-list.component.scss'
})
export class OpbCompanyListComponent {
  @ViewChild(GenericListComponent) genericList?: GenericListComponent<OpbCompanyDto>;

  companies: OpbCompanyDto[] = [];
  totalItems = 0;
  currentPage = 0;
  pageSize = 10;
  isSearchVisible = true;
  isExporting = false;

  currentSearchFilter: OpbCompanySearchFilter = {
    measurement: Helpers.defaultMeasurementYear(),
    isActive: true,
    isTransferred: false
  };
  private sortBy?: string;
  private ascending = true;

  columnDefinitions: ColumnDefinition[] = [
    { propertyName: 'name', displayName: 'Name' },
    { propertyName: 'countryName', displayName: 'Country' },
    { propertyName: 'boardSystem', displayName: 'Unitary/Dual', dataType: 'enum', enumType: { 1: 'Unitary', 2: 'Dual' } },
    { propertyName: 'stockSymbol', displayName: 'Stock Symbol' },
    { propertyName: 'sectorName', displayName: 'Sub Sector' },
    { propertyName: 'currencyName', displayName: 'Currency' },
    { propertyName: 'stockExchangeName', displayName: 'Stock Market' }
  ];

  constructor(
    private router: Router,
    private opbCompanyService: OpbCompanyService,
    private confirmationDialog: ConfirmationDialogService,
    private toast: HotToastService,
    private spinnerService: NgxSpinnerService,
    private store: Store<IAppState>,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.store.select(selectOpbCompanyListFullState).pipe(
      filter(state => state !== undefined && state !== null),
      take(1)
    ).subscribe(state => {
      this.currentSearchFilter = {
        ...state.searchFilter,
        measurement: state.searchFilter.measurement
          ? state.searchFilter.measurement
          : Helpers.defaultMeasurementYear()
      };
      this.currentPage = state.currentPage - 1;
      this.pageSize = state.pageSize;
      this.sortBy = state.sortBy;
      this.ascending = state.ascending;

      this.loadCompanies();
    });
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active;
    this.ascending = sort.direction === 'asc';
    this.currentPage = 0;

    this.store.dispatch(new SetOpbCompanySort({ sortBy: sort.active, ascending: this.ascending }));

    this.loadCompanies();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;

    this.store.dispatch(new SetOpbCompanyPagination({ currentPage: event.pageIndex + 1, pageSize: event.pageSize }));

    this.loadCompanies();
  }

  onCreateCompany(): void {
    this.router.navigate(['/companies', 'opb-create']);
  }

  onImportCsv(): void {
    this.dialog.open(OpbCompanyCsvImportDialogComponent, {
      maxWidth: '90vw',
      autoFocus: false,
      restoreFocus: false
    }).afterClosed().subscribe(imported => {
      if (imported) {
        this.loadCompanies();
      }
    });
  }

  onEditCompany(company: OpbCompanyDto): void {
    this.router.navigate(['/companies', 'opb-edit', company.id]);
  }

  onTransferCompany(company: OpbCompanyDto): void {
    const year = this.currentSearchFilter.measurement
      ? parseInt(this.currentSearchFilter.measurement, 10)
      : Number(Helpers.defaultMeasurementYear());

    this.confirmationDialog.openConfirmationDialog(
      'Transfer to Companies',
      `Are you sure you want to transfer "${company.name}" to the Companies list?\n\n` +
      `This will create a new Company entry based on this OPB company's data for measurement year ${year}. ` +
      `The OPB company will be marked as transferred and can no longer be modified.`,
      'Transfer',
      'Cancel',
      'swap_horiz',
      'clear'
    ).subscribe(confirmed => {
      if (!confirmed) return;
      this.spinnerService.show();
      this.opbCompanyService.convertToCompany(company.id, { year }).subscribe({
        next: () => {
          this.toast.success(`"${company.name}" transferred to Companies successfully`);
          this.loadCompanies();
        },
        error: () => {
          this.toast.error(`Failed to transfer "${company.name}"`);
          this.spinnerService.hide();
        }
      });
    });
  }

  onDeleteCompany(company: OpbCompanyDto): void {
    this.confirmationDialog.openDeleteConfirmationDialog(company.name).subscribe(confirmed => {
      if (!confirmed) return;
      this.spinnerService.show();
      this.opbCompanyService.deleteOpbCompany(company.id).subscribe({
        next: () => {
          this.toast.success(`"${company.name}" deleted successfully`);
          this.loadCompanies();
        },
        error: () => {
          this.toast.error(`Failed to delete "${company.name}"`);
          this.spinnerService.hide();
        }
      });
    });
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onSearchFilterChange(filter: OpbCompanySearchFilter): void {
    this.currentSearchFilter = filter;
    this.currentPage = 0;

    this.store.dispatch(new SetOpbCompanySearchFilter(filter));

    if (this.genericList?.paginator) {
      this.genericList.paginator.pageIndex = 0;
    }

    this.loadCompanies();
  }

  onExportToCsv(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.spinnerService.show();

    this.opbCompanyService.exportOpbCompaniesToCsv(this.buildSearchRequest()).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `opb_companies_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.isExporting = false;
        this.toast.success('OPB companies exported successfully');
      },
      error: (error) => {
        console.error('Error exporting OPB companies:', error);
        this.toast.error('Error exporting OPB companies');
        this.spinnerService.hide();
        this.isExporting = false;
      }
    });
  }

  private buildSearchRequest(): OpbCompaniesSearchRequest {
    return {
      pageNumber: this.currentPage + 1,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending,
      countryId: this.currentSearchFilter.country,
      name: this.currentSearchFilter.name,
      year: this.currentSearchFilter.measurement ? parseInt(this.currentSearchFilter.measurement, 10) : undefined,
      isActive: this.currentSearchFilter.isActive,
      isTransferred: this.currentSearchFilter.isTransferred
    };
  }

  private loadCompanies(): void {
    this.spinnerService.show();

    this.opbCompanyService.searchOpbCompanies(this.buildSearchRequest()).subscribe({
      next: (response) => {
        this.companies = response.data;
        this.totalItems = response.totalCount;
        this.currentPage = response.page - 1;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading OPB companies:', error);
        this.toast.error('Failed to load OPB companies');
        this.spinnerService.hide();
      }
    });
  }
}
